// Pure-function planners for the cook-session lifecycle.
//
// Two responsibilities:
//
//   buildInitialUsedItems(recipe, pantry, session, { ingredientInfo })
//     Seeds the "what did you use?" phase. Builds one row per recipe
//     ingredient with: matched pantry row(s), default selection,
//     called-for amount/unit, skip flag. Runs the full pairing pass
//     (state-aware, curated-tier-aware) so the deduction screen
//     starts from the same row CookMode displayed on prep.
//
//   buildRemovalPlan(usedItems, extraRemovals, pantry)
//     Flattens user picks into a list of pantry-row decrements. Walks
//     a FIFO sibling cascade so demand exceeding one row falls through
//     to the next stocked instance. Substitute-aware: when the chosen
//     row's canonical differs from the recipe's, conversion uses the
//     PANTRY row's canonical (the physical ingredient's ladder /
//     density) so cross-canonical bridges land on the right answer.
//
// Both extracted from CookComplete.jsx during the 2026-05-03 size
// audit — pure helpers shouldn't sit inside a 2,600-line component
// when callers just want "give me the seeded rows" or "give me the
// decrement plan."

import { findIngredient } from "../data/ingredients";
import { convertWithBridge, decrementRow, planInstanceDecrement } from "./unitConvert";
import { parseAmountString } from "./nutrition";
import { resolvesToSameCanonical, pairRecipeIngredients, resolveNameToCanonicalId } from "./recipePairing";
import { identityKey } from "./pantryFormat";
import { isAddedToBowl } from "./useCookSession";

// Build one row per recipe ingredient for the "what did you use" phase.
// Each row carries:
//   recipeIng    — the original entry from recipe.ingredients[]
//   canonical    — the INGREDIENTS registry def (null for untracked free-text)
//   matches      — pantry rows for this ingredient (kind='ingredient' only)
//   selectedRowId— which pantry row the user is drawing from (first match by
//                  default; the multi-match picker will swap it in a follow-up)
//   usedAmount / usedUnit — initial estimate from recipe.qty, editable in the
//                  phase. Untracked rows have both null and a ✕-only card.
//   skipped      — user tapped ✕. Final removal plan ignores skipped rows.
export function buildInitialUsedItems(recipe, pantry, session, options = {}) {
  const ingredients = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  const list = Array.isArray(pantry) ? pantry : [];
  // Mid-cook counter-list check-offs (addedToBowl overrides) seed the
  // deduction screen so the user isn't asked the same question twice.
  // Three-valued per row:
  //   "checked"   — the cook ticked this ingredient into the bowl;
  //                 render pre-confirmed (collapsed summary).
  //   "unchecked" — the cook used the counter list but never ticked
  //                 this one; flag for review, do NOT silently skip
  //                 (under-deduction guard — forgetting to tick is
  //                 more common than not using).
  //   "none"      — the cook never touched the counter list this
  //                 session; seed exactly like before, no flags.
  const anyCheckoffs = ingredients.some((_, idx) => isAddedToBowl(session, idx));
  // Run the SAME pairer CookMode uses so closest-match substitutes
  // (recipe wants Mozzarella, user has Parmesan, curated tier-aware
  // match picks Pecorino with "Sharper, saltier" note) carry through
  // to the deduction screen. Previously this function filtered with
  // resolvesToSameCanonical alone — exact canonical — so a substitute
  // was invisible here and the row rendered "NOT IN PANTRY" even
  // after CookMode paired it live. Pass ingredientInfo through so
  // curated subs read DB-overlay enrichment for synthetic canonicals.
  const pairings = pairRecipeIngredients(ingredients, list, {
    ingredientInfo: options.ingredientInfo || null,
  });
  return ingredients.map((ing, idx) => {
    // Resolve canonical with name-fallback. Recipes don't always carry
    // ingredientId — AI generations, free-text drafts, and imports
    // routinely arrive with only a name. Without this fallback every
    // such row reads "UNTRACKED" in the UI, the amount slider doesn't
    // render, and the row is silently skipped by buildRemovalPlan —
    // the user can neither edit nor decrement anything. Mirrors the
    // same chain pairRecipeIngredients uses (recipePairing.js:364).
    const ingCanonId = ing.ingredientId
      || resolveNameToCanonicalId(ing.item || ing.name || "")
      || null;
    const canonical = ingCanonId ? findIngredient(ingCanonId) : null;
    // Match universe for this ingredient row — the cascade that
    // deduction and the swap picker choose from. Three tiers:
    //   1. Explicit canonical family (exact match after alias
    //      redirect, no hub widening — heavy_cream and milk stay
    //      distinct).
    //   2. Pairer's paired row (same canonical / hub) and
    //      closestMatch row (substitute band) — surfaces rows that
    //      pairRecipeIngredients already decided were valid.
    //   3. Deduped, sorted FIFO by expires_at so the oldest-
    //      expiring instance is the default (consume before spoil).
    const familyMatches = ingCanonId
      ? list.filter(p =>
          resolvesToSameCanonical(p.ingredientId, ingCanonId) &&
          (p.kind || "ingredient") === "ingredient" &&
          Number(p.amount) > 0
        )
      : [];
    const pairing = pairings[idx] || null;
    const suggested = [pairing?.paired, pairing?.closestMatch].filter(Boolean);
    // Composition-match candidates: compound rows (kind !== "ingredient")
    // whose ingredients[] genuinely contains the target canonical
    // AND whose category is in the same family as the target. User's
    // insight: "wouldn't it look Dairy → Cheese → what contains
    // mozzarella, not Freezer → Frozen Meals → pizza?" Exactly —
    // same-category gate uses the pantry-taxonomy lineage as a
    // scoop-ability proxy:
    //
    //   mozzarella (dairy) + italian_cheese_dip (dairy)    → match ✓
    //   mozzarella (dairy) + frozen_pizza (frozen/entree)  → reject ✗
    //
    // No per-canonical scoopable flag needed; the category hierarchy
    // already tells us whether the compound lives in the "I can
    // scoop this" aisle or the "I'm eating this as a meal" aisle.
    const compositionMatches = ingCanonId
      ? (list || []).filter(p => {
          if (!p || Number(p.amount) <= 0) return false;
          if ((p.kind || "ingredient") === "ingredient") return false;
          // A finished leftover MEAL (kind "meal", measured in servings)
          // is something you EAT, not an ingredient you scoop from. Its
          // `ingredients[]` array exists only for recipe-MATCHING ("what
          // can I make with last night's alfredo"), and reusing it as a
          // deduction source produced nonsense: a new recipe's "4 large
          // eggs" slot pairing to "Leftover Ajitsuke Tamago" with an
          // unconvertible serving→count, "8 tbsp" pairing to "Leftover
          // Alfredo," etc. You do not pull raw eggs back out of a cooked
          // egg dish. Component leftovers the cook genuinely scoops from
          // (marinara, stock, dough) are stored as kind "ingredient" and
          // come through familyMatches, so they're unaffected.
          if ((p.kind || "ingredient") === "meal") return false;
          const ids = Array.isArray(p.ingredients) ? p.ingredients : [];
          if (!ids.some(id => resolvesToSameCanonical(id, ingCanonId))) return false;
          // Category-lineage gate — scoop from the cheese aisle, not
          // the frozen-meals aisle. When the target canonical has no
          // category (rare — synthetic / unenriched), allow through;
          // when the pantry row has no category, fall back to item
          // category (set at add-time from tile).
          const ingCanonCat = canonical?.category || null;
          const pCanon = p.ingredientId ? findIngredient(p.ingredientId) : null;
          const pCat = pCanon?.category || p.category || null;
          if (ingCanonCat && pCat && ingCanonCat !== pCat) return false;
          return true;
        })
      : [];
    const seen = new Set();
    const matches = [...familyMatches, ...suggested, ...compositionMatches]
      .filter(m => {
        if (!m || !m.id || seen.has(m.id)) return false;
        seen.add(m.id);
        return Number(m.amount) > 0;
      })
      .sort((a, b) => {
        const ea = a.expiresAt ? new Date(a.expiresAt).getTime() : Infinity;
        const eb = b.expiresAt ? new Date(b.expiresAt).getTime() : Infinity;
        if (ea !== eb) return ea - eb;
        const pa = a.purchasedAt ? new Date(a.purchasedAt).getTime() : 0;
        const pb = b.purchasedAt ? new Date(b.purchasedAt).getTime() : 0;
        return pa - pb;
      });
    // Priority order for the default selection:
    //   1. Session override — user's explicit choice from cook-prep
    //      or AIRecipe tweak, authoritative.
    //   2. pairing.paired — Tier 1 primary canonical match (state-aware
    //      now that recipePairing.js gates Tier 1 by recipe state).
    //   3. State-aware FIFO from the family universe — when the recipe
    //      asks for ground chicken and pairing.paired wasn't bound
    //      (legacy pantry without state set, or wrong-state rows),
    //      prefer a row whose state matches the recipe's intent.
    //   4. Composition match — compound row whose ingredients[]
    //      contains the target. Prefers "cheese dip that has mozz"
    //      over "queso dip as a generic cheese substitute." Only
    //      fires when no primary-canonical row exists.
    //   5. pairing.closestMatch — substitute band (curated tier-aware
    //      via recipePairing's findCuratedSub, then hub/category).
    //   6. FIFO first from the family universe.
    const overrideRowId = session?.overrides?.[idx]?.pantryItemId || null;
    const overrideRow = overrideRowId
      ? list.find(p => p.id === overrideRowId) || null
      : null;
    const ingState = ing.state || null;
    const stateMatched = ingState
      ? matches.find(m => (m.state || null) === ingState) || null
      : null;
    const defaultMatch = overrideRow
      || pairing?.paired
      || stateMatched
      || compositionMatches[0]
      || pairing?.closestMatch
      || matches[0]
      || null;
    // Resolve the recipe's called-for quantity so the slider + amount
    // field prefill instead of loading at 0 (seen in the wild: "What
    // did you use?" screen with every slider pinned to 0 because
    // AI-drafted recipes only carry the display string "2 tbsp" and
    // not the structured qty {amount, unit}). Three-tier resolve:
    //   1. ing.qty — explicit structured qty (bundled recipes, older
    //      drafts that kept it)
    //   2. parseAmountString(ing.amount, canonical) — parse "2 tbsp"
    //      or "¼ cup" or "8" off the display string using the unit
    //      ladder of the matched canonical
    //   3. null — untracked / free-text, slider stays manual
    const recipeQty = ing.qty || (canonical ? parseAmountString(ing.amount, canonical) : null);

    // Display unit policy: RECIPE UNIT WINS.
    //
    // Recipe called for "¾ cup" → show everything in cups. Pantry
    // row happens to be stored in quarts → convert quarts to cups
    // for display, don't force the user to read the pantry's unit.
    // Previous behavior (pantry unit wins) was the bug the user
    // flagged: recipe in cups but meter showing quarts + pints,
    // too many units on screen.
    //
    // Only fall back to the pantry row's unit when (a) the recipe
    // had no parseable qty, or (b) the recipe's unit isn't in the
    // canonical's ladder (e.g. garlic recipe says "2 tbsp" but
    // garlic's ladder is [clove, head] — can't display "tbsp").
    // That fallback path is handled by the coherence guard below.
    let displayAmount = recipeQty?.amount ?? null;
    let displayUnit   = recipeQty?.unit   ?? (defaultMatch?.unit ?? null);

    // Coherence guard: usedUnit MUST be in the canonical's ladder,
    // otherwise the dropdown shows the first option (browser fallback
    // when <select value> isn't a valid option) while state stays on
    // an invalid unit — labels render in the stale unit, conversions
    // fail silently. Seen when a garlic pantry row was stored in oz
    // (outside garlic's [clove, head] ladder) and a recipe asked for
    // "2 tbsp" that didn't parse. If the current displayUnit isn't
    // valid, fall back to the canonical's defaultUnit and re-convert
    // the displayed amount into that unit so the number agrees with
    // what the label will read.
    const ladderHas = (u) => !!canonical?.units?.some(x => x.id === u);
    if (canonical && displayUnit && !ladderHas(displayUnit)) {
      const targetUnit = canonical.defaultUnit && ladderHas(canonical.defaultUnit)
        ? canonical.defaultUnit
        : (canonical.units?.[0]?.id || null);
      if (targetUnit) {
        // Try to carry the number across, if we can find any valid
        // source qty to convert from.
        const sourceQty = recipeQty && ladderHas(recipeQty.unit)
          ? recipeQty
          : (displayAmount != null && ladderHas(displayUnit)
              ? { amount: displayAmount, unit: displayUnit }
              : null);
        if (sourceQty) {
          const res = convertWithBridge(sourceQty, targetUnit, canonical, defaultMatch);
          displayAmount = res.ok ? Number(res.value.toFixed(3)) : null;
        } else {
          // No convertible source (e.g. "2 tbsp" for garlic with only
          // clove/head ladder). Clear the number — user types in the
          // picker — but ensure the unit is valid so the dropdown and
          // labels agree.
          displayAmount = null;
        }
        displayUnit = targetUnit;
      }
    }

    return {
      idx,
      recipeIng: ing,
      canonical,
      matches,
      skipped: false,
      selectedRowId: defaultMatch?.id || null,
      usedAmount: displayAmount,
      usedUnit:   displayUnit,
      checkoffState: anyCheckoffs
        ? (isAddedToBowl(session, idx) ? "checked" : "unchecked")
        : "none",
    };
  });
}

// Flatten usedItems + extraRemovals into a single list of decrement
// instructions the confirm-removal screen can render and the final save()
// can apply. Each entry resolves to a specific pantry row + how much to
// subtract in that row's own unit, so the caller never needs to reason
// about conversion again.
//
// Returns:
//   [{ pantryRowId, pantryRow, ingredient, used: {amount, unit},
//      newAmount,   — row.amount after decrement (null if un-convertible)
//      convertible, — false when used.unit isn't in the ingredient's ladder
//      source: "recipe" | "added" }]
export function buildRemovalPlan(usedItems, extraRemovals, pantry) {
  const lookup = (id) => (pantry || []).find(p => p.id === id) || null;
  // Siblings of `seed` = rows sharing identity (canonical + state +
  // composition + name) with a positive amount, sorted FIFO so the
  // cascade consumes oldest-expires first. Excludes the seed itself —
  // the caller handles it as the head of the cascade.
  const siblingsForCascade = (seed) => {
    if (!seed) return [];
    const key = identityKey(seed);
    return (pantry || [])
      .filter(p => p.id !== seed.id && Number(p.amount) > 0 && identityKey(p) === key)
      .sort((a, b) => {
        const ea = a.expiresAt ? new Date(a.expiresAt).getTime() : Infinity;
        const eb = b.expiresAt ? new Date(b.expiresAt).getTime() : Infinity;
        if (ea !== eb) return ea - eb;
        const pa = a.purchasedAt ? new Date(a.purchasedAt).getTime() : 0;
        const pb = b.purchasedAt ? new Date(b.purchasedAt).getTime() : 0;
        return pa - pb;
      });
  };
  const out = [];
  for (const row of usedItems) {
    if (row.skipped || !row.selectedRowId) continue;
    if (row.usedAmount == null || !Number.isFinite(Number(row.usedAmount))) continue;
    if (!row.usedUnit) continue;
    const pantryRow = lookup(row.selectedRowId);
    if (!pantryRow) continue;
    const used = { amount: Number(row.usedAmount), unit: row.usedUnit };
    // Display prefers the matched pantry row's label so the user sees
    // "Tostitos Queso" on the confirm-removal screen, not the recipe's
    // "Fresh Mozzarella" — they're actually decrementing the queso.
    // Same priority chain as the cook-prep screen uses.
    const pantryCanon = pantryRow.ingredientId ? findIngredient(pantryRow.ingredientId) : null;
    const recipeAsk = row.canonical?.name || row.recipeIng.item || null;
    const isSubstitute = !!pantryRow.ingredientId
      && !!row.canonical?.id
      && !resolvesToSameCanonical(pantryRow.ingredientId, row.canonical.id);
    const displayName = pantryRow.name
      || pantryCanon?.name
      || row.canonical?.name
      || row.recipeIng.item
      || "Ingredient";
    const displayEmoji = pantryRow.emoji
      || pantryCanon?.emoji
      || row.canonical?.emoji
      || row.recipeIng.emoji
      || "🥣";
    // Cascade: consume the selected row first, then fall through to
    // sibling instances FIFO when demand exceeds the seed's stock.
    // Preserves the user's explicit "use this row" pick — we just
    // cascade AFTER they've drained it.
    //
    // SUBSTITUTE-AWARE CANONICAL: when the chosen pantry row is a
    // substitute (different canonical than the recipe asked for),
    // unit math MUST use the pantry row's canonical, not the
    // recipe's. Otherwise convertWithBridge looks up density / unit
    // ladder for the wrong ingredient — e.g. a recipe asking for
    // mozzarella but the user pulled queso would route the
    // conversion through mozzarella's (missing) density even though
    // every physical row in the cascade is queso. Falls back to
    // recipe.canonical when the pantry row has no canonical of its
    // own (rare — pre-canonical free-text rows).
    const cascadeCanonical = isSubstitute && pantryCanon
      ? pantryCanon
      : row.canonical;
    const cascade = cascadeCanonical
      ? planInstanceDecrement([pantryRow, ...siblingsForCascade(pantryRow)], used, cascadeCanonical)
      : null;
    if (!cascade || cascade.entries.length === 0) {
      // Un-convertible (unit not in ladder) or no canonical — keep
      // the legacy single-entry, non-convertible signal so the
      // confirm screen still renders its red warning chip.
      out.push({
        pantryRowId: pantryRow.id,
        pantryRow,
        ingredient: cascadeCanonical,
        used,
        newAmount: null,
        convertible: false,
        source: "recipe",
        sourceIdx: row.idx,
        displayName,
        displayEmoji,
        subFor: isSubstitute ? recipeAsk : null,
      });
      continue;
    }
    for (const entry of cascade.entries) {
      out.push({
        pantryRowId: entry.row.id,
        pantryRow: entry.row,
        ingredient: cascadeCanonical,
        used: { amount: entry.consumedAmount, unit: entry.unit },
        newAmount: entry.newAmount,
        convertible: true,
        source: "recipe",
        sourceIdx: row.idx,
        displayName,
        displayEmoji,
        subFor: isSubstitute ? recipeAsk : null,
      });
    }
  }
  for (const extra of extraRemovals) {
    if (extra.amount == null || !Number.isFinite(Number(extra.amount))) continue;
    const pantryRow = lookup(extra.pantryRowId);
    if (!pantryRow) continue;
    const canonical = extra.ingredientId ? findIngredient(extra.ingredientId) : null;
    const used = { amount: Number(extra.amount), unit: extra.unit };
    // Meal rows (kind='meal') have no ingredientId / canonical, but also no
    // unit ambiguity — everything is "serving" on both sides — so we can
    // subtract directly without going through the converter.
    let newAmount;
    if (canonical) {
      newAmount = decrementRow(pantryRow, used, canonical);
    } else if (extra.unit === pantryRow.unit) {
      newAmount = Math.max(0, Number(pantryRow.amount) - used.amount);
    } else {
      newAmount = null;
    }
    out.push({
      pantryRowId: pantryRow.id,
      pantryRow,
      ingredient: canonical,
      used,
      newAmount,
      convertible: newAmount != null,
      source: "added",
      displayName: extra.name,
      displayEmoji: extra.emoji,
    });
  }
  return out;
}
